import React, { useEffect, useState } from 'react';
import { 
  UserIcon, 
  MicrophoneIcon,
  SparklesIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { apiService, Panelist } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const Panelists: React.FC = () => {
  const [panelists, setPanelists] = useState<Panelist[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPanelists = async () => {
      try {
        const data = await apiService.getPanelists();
        setPanelists(data);
      } catch (error) {
        console.error('Error fetching panelists:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPanelists();
  }, []);

  const getGenderIcon = (gender: string) => {
    switch (gender.toLowerCase()) {
      case 'male':
        return '👨';
      case 'female':
        return '👩';
      case 'non-binary':
        return '👤';
      default:
        return '👤';
    }
  };

  const getTTSProviderIcon = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'openai':
        return '🤖';
      case 'azure':
        return '☁️';
      case 'google':
        return '🔍';
      default:
        return '🎤';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Meet Your AI Panelists
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          Each panelist has their own unique personality, expertise, and speaking style. 
          They bring diverse perspectives to every news discussion.
        </p>
      </div>

      {/* Panelists Grid */}
      {panelists.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {panelists.map((panelist) => (
            <div
              key={panelist._id}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              {/* Panelist Header */}
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full overflow-hidden flex items-center justify-center text-white text-2xl font-bold">
                  {(panelist as any).headshotUrl ? (
                    <img
                      src={apiService.getPanelistHeadshotUrl(panelist._id)}
                      alt={panelist.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="w-full h-full flex items-center justify-center"
                    style={{ display: (panelist as any).headshotUrl ? 'none' : 'flex' }}
                  >
                    {panelist.name.charAt(0)}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                    {panelist.name}
                  </h3>
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <span>{getGenderIcon(panelist.gender)}</span>
                    <span>{panelist.gender}</span>
                    {panelist.age && (
                      <>
                        <span>•</span>
                        <span>{panelist.age} years old</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Character & Background */}
              <div className="mb-4">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <span className="font-semibold">Character:</span> {panelist.character}
                </p>
                {panelist.background && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                    <span className="font-semibold">Background:</span> {panelist.background}
                  </p>
                )}
              </div>

              {/* Expertise */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Expertise:</p>
                <div className="flex flex-wrap gap-1">
                  {panelist.expertise.map((skill, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Voice Settings */}
              <div className="mb-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <MicrophoneIcon className="w-4 h-4" />
                  <span className="font-semibold">Voice Settings</span>
                </div>
                <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    <span>{getTTSProviderIcon(panelist.ttsProvider)}</span>
                    <span>Provider: {panelist.ttsProvider}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <GlobeAltIcon className="w-3 h-3" />
                    <span>Language: {panelist.languageCode}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SparklesIcon className="w-3 h-3" />
                    <span>Voice: {panelist.voiceName}</span>
                  </div>
                </div>
              </div>

              {/* Discussion Style */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Discussion Style:</p>
                <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                  {panelist.discussionStyle.tone && (
                    <div><span className="font-medium">Tone:</span> {panelist.discussionStyle.tone}</div>
                  )}
                  {panelist.discussionStyle.pace && (
                    <div><span className="font-medium">Pace:</span> {panelist.discussionStyle.pace}</div>
                  )}
                  {panelist.discussionStyle.style && (
                    <div><span className="font-medium">Style:</span> {panelist.discussionStyle.style}</div>
                  )}
                  {panelist.discussionStyle.approach && (
                    <div><span className="font-medium">Approach:</span> {panelist.discussionStyle.approach}</div>
                  )}
                  {panelist.discussionStyle.speaking_style && (
                    <div><span className="font-medium">Speaking:</span> {panelist.discussionStyle.speaking_style}</div>
                  )}
                  {panelist.discussionStyle.formality && (
                    <div><span className="font-medium">Formality:</span> {panelist.discussionStyle.formality}</div>
                  )}
                </div>
              </div>

              {/* OpenAI Voice Settings */}
              {panelist.openaiVoiceSettings && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">OpenAI Settings:</p>
                  <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                    {panelist.openaiVoiceSettings.speed && (
                      <div><span className="font-medium">Speed:</span> {panelist.openaiVoiceSettings.speed}</div>
                    )}
                    {panelist.openaiVoiceSettings.format && (
                      <div><span className="font-medium">Format:</span> {panelist.openaiVoiceSettings.format}</div>
                    )}
                    {panelist.openaiVoiceSettings.instructions && (
                      <div className="line-clamp-2">
                        <span className="font-medium">Instructions:</span> {panelist.openaiVoiceSettings.instructions}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  panelist.isPublic 
                    ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}>
                  {panelist.isPublic ? 'Public' : 'Private'}
                </span>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Created {new Date(panelist.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <UserIcon className="w-12 h-12 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            No Panelists Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
            There are no panelists configured at the moment. Panelists will appear here once they're added to the system.
          </p>
        </div>
      )}
    </div>
  );
};

export default Panelists;

